import { useDraggable } from '@dnd-kit/core';
import type { CardDto } from '@konvert/shared';
import { formatMinutesRu } from '@konvert/domain';
import { haptics } from '../lib/telegram.js';
import { t } from '../lib/i18n.js';

const ENERGY_CLASS: Record<string, string> = {
  green: 'bg-green',
  yellow: 'bg-amber',
  red: 'bg-brick',
};

const KIND_STAMP: Record<CardDto['kind'], string> = {
  task: '✓',
  project: '▣',
  meeting: '◷',
  note: '✎',
  reminder: '!',
};

export function CardTile({
  card,
  onOpen,
  conflicting,
  threshold,
}: {
  card: CardDto;
  onOpen: (card: CardDto) => void;
  conflicting?: boolean;
  threshold: number;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { card },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0) rotate(-1deg)` }
    : undefined;

  const time =
    card.kind === 'meeting' && card.start_at
      ? new Date(card.start_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      : card.est_minutes
        ? formatMinutesRu(card.est_minutes)
        : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onPointerDown={(event) => {
        haptics.pick();
        listeners?.onPointerDown?.(event);
      }}
      className={`paper-card relative px-3 py-2.5 select-none ${
        isDragging ? 'shadow-lifted opacity-90' : ''
      }`}
    >
      <button
        type="button"
        data-testid="card"
        data-card-id={card.id}
        onClick={() => onOpen(card)}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="mt-0.5 text-xs text-ink-soft" aria-hidden>
          {KIND_STAMP[card.kind]}
        </span>
        <span className="flex-1">
          <span className={`block text-[15px] leading-snug ${card.status === 'done' ? 'line-through text-ink-soft' : 'text-ink'}`}>
            {card.is_private ? '[скрытая карточка]' : card.title}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
            {time ? <span>{time}</span> : null}
            {card.energy ? (
              <span className={`inline-block h-2.5 w-2.5 rounded-[2px] ${ENERGY_CLASS[card.energy]}`} />
            ) : null}
            {card.reschedule_count >= threshold ? (
              <span>
                {t('card.rescheduled')} {card.reschedule_count}
              </span>
            ) : null}
            {conflicting ? <span className="text-brick">◷ пересечение</span> : null}
            {card.status === 'waiting' ? <span>ожидание</span> : null}
          </span>
        </span>
      </button>
    </div>
  );
}
